import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useCMS } from '../../context/CMSContext';
import { Video, Save, Link, Clock, MessageSquare, CheckCircle, ToggleLeft, ToggleRight, Calendar, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBookingSettingsPage = () => {
  const { token } = useCMS();
  const [settings, setSettings] = useState({
    microsoft_bookings_enabled: true,
    microsoft_bookings_url: '',
    teams_meeting_enabled: false,
    teams_meeting_link: '',
    default_duration: 30,
    timezone: 'AEST',
    confirmation_message: 'Thank you for booking! You will receive a confirmation email shortly.'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cms/booking-settings`);
      if (res.ok) {
        const data = await res.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (e) {
      console.error('Failed to fetch booking settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/booking-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (res.ok) {
        toast.success('Booking settings saved successfully!');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (e) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-text-secondary">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Booking Settings</h1>
        <p className="text-text-secondary">Configure booking system and meeting links</p>
      </div>

      {/* Microsoft Bookings */}
      <Card className="border-border-line border-2 border-tech-blue/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center">
                <Calendar className="mr-2 text-tech-blue" size={20} />
                Microsoft Bookings
              </CardTitle>
              <CardDescription>
                Embed Microsoft Bookings calendar on your booking page
              </CardDescription>
            </div>
            {/* Enable/Disable Toggle */}
            <button
              onClick={() => setSettings({ ...settings, microsoft_bookings_enabled: !settings.microsoft_bookings_enabled })}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                settings.microsoft_bookings_enabled 
                  ? 'bg-green-100 text-green-700 border-2 border-green-300' 
                  : 'bg-gray-100 text-gray-500 border-2 border-gray-200'
              }`}
            >
              {settings.microsoft_bookings_enabled ? (
                <>
                  <ToggleRight size={20} />
                  <span className="font-medium">Enabled</span>
                </>
              ) : (
                <>
                  <ToggleLeft size={20} />
                  <span className="font-medium">Disabled</span>
                </>
              )}
            </button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings.microsoft_bookings_enabled && (
            <>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  <Link size={14} className="inline mr-1" />
                  Microsoft Bookings URL
                </label>
                <input
                  type="url"
                  value={settings.microsoft_bookings_url}
                  onChange={(e) => setSettings({ ...settings, microsoft_bookings_url: e.target.value })}
                  placeholder="https://outlook.office.com/bookwithme/user/..."
                  className="w-full px-4 py-3 border border-border-line rounded-lg focus:ring-2 focus:ring-tech-blue focus:border-transparent"
                />
                <p className="text-xs text-text-secondary mt-2">
                  Get this from Microsoft Bookings → Share → Copy booking page link
                </p>
              </div>

              {settings.microsoft_bookings_url && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-blue-700">
                      <CheckCircle size={16} />
                      <span className="font-medium">Microsoft Bookings configured</span>
                    </div>
                    <a 
                      href={settings.microsoft_bookings_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                    >
                      <ExternalLink size={14} />
                      Preview
                    </a>
                  </div>
                  <p className="text-sm text-blue-600 mt-2">
                    The Microsoft Bookings calendar will be embedded on your /booking page.
                  </p>
                </div>
              )}
            </>
          )}

          {!settings.microsoft_bookings_enabled && (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-gray-600 text-sm">
                Microsoft Bookings is currently <strong>disabled</strong>. Enable it to use the embedded booking calendar.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Teams Meeting Link */}
      <Card className="border-border-line">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center">
                <Video className="mr-2 text-[#0078d4]" size={20} />
                Microsoft Teams Meeting
              </CardTitle>
              <CardDescription>
                Include Teams meeting link in booking confirmation and reminder emails
              </CardDescription>
            </div>
            {/* Enable/Disable Toggle */}
            <button
              onClick={() => setSettings({ ...settings, teams_meeting_enabled: !settings.teams_meeting_enabled })}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                settings.teams_meeting_enabled 
                  ? 'bg-green-100 text-green-700 border-2 border-green-300' 
                  : 'bg-gray-100 text-gray-500 border-2 border-gray-200'
              }`}
            >
              {settings.teams_meeting_enabled ? (
                <>
                  <ToggleRight size={20} />
                  <span className="font-medium">Enabled</span>
                </>
              ) : (
                <>
                  <ToggleLeft size={20} />
                  <span className="font-medium">Disabled</span>
                </>
              )}
            </button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!settings.teams_meeting_enabled && (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-gray-600 text-sm">
                Teams meeting is currently <strong>disabled</strong>. Enable it to include the meeting link in emails.
              </p>
            </div>
          )}
          
          {settings.teams_meeting_enabled && (
            <>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  <Link size={14} className="inline mr-1" />
                  Teams Meeting URL
                </label>
                <input
                  type="url"
                  value={settings.teams_meeting_link}
                  onChange={(e) => setSettings({ ...settings, teams_meeting_link: e.target.value })}
                  placeholder="https://teams.microsoft.com/l/meetup-join/..."
                  className="w-full px-4 py-3 border border-border-line rounded-lg focus:ring-2 focus:ring-tech-blue focus:border-transparent"
                />
                <p className="text-xs text-text-secondary mt-2">
                  To get this link: Open Microsoft Teams → Create a meeting → Copy the "Join" link
                </p>
              </div>

              {settings.teams_meeting_link && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 text-blue-700 mb-2">
                    <CheckCircle size={16} />
                    <span className="font-medium">Teams link configured</span>
                  </div>
                  <p className="text-sm text-blue-600">
                    The "Join Teams Meeting" button will appear in booking confirmation and reminder emails.
                  </p>
                </div>
              )}

              {!settings.teams_meeting_link && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-amber-700 text-sm">
                    ⚠️ Teams is enabled but no link is set. Please add your Teams meeting URL above.
                  </p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* General Booking Settings */}
      <Card className="border-border-line">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Clock className="mr-2 text-care-teal" size={20} />
            General Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Default Duration (minutes)
              </label>
              <select
                value={settings.default_duration}
                onChange={(e) => setSettings({ ...settings, default_duration: parseInt(e.target.value) })}
                className="w-full px-4 py-3 border border-border-line rounded-lg focus:ring-2 focus:ring-tech-blue focus:border-transparent"
              >
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>60 minutes</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Timezone
              </label>
              <select
                value={settings.timezone}
                onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                className="w-full px-4 py-3 border border-border-line rounded-lg focus:ring-2 focus:ring-tech-blue focus:border-transparent"
              >
                <option value="AEST">AEST (Sydney)</option>
                <option value="AEDT">AEDT (Sydney DST)</option>
                <option value="AWST">AWST (Perth)</option>
                <option value="ACST">ACST (Adelaide)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              <MessageSquare size={14} className="inline mr-1" />
              Confirmation Message
            </label>
            <textarea
              value={settings.confirmation_message}
              onChange={(e) => setSettings({ ...settings, confirmation_message: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 border border-border-line rounded-lg focus:ring-2 focus:ring-tech-blue focus:border-transparent"
              placeholder="Message shown after successful booking..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-tech-blue hover:bg-tech-blue/90"
        >
          <Save size={16} className="mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* Preview Section */}
      {settings.teams_meeting_enabled && settings.teams_meeting_link && (
        <Card className="border-border-line bg-gray-50">
          <CardHeader>
            <CardTitle className="text-lg">Email Preview</CardTitle>
            <CardDescription>How the Teams link will appear in emails</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-white p-6 rounded-lg border border-gray-200 max-w-md mx-auto">
              <div className="bg-[#e0f2fe] border-2 border-[#0284c7] p-5 rounded-lg text-center">
                <h3 className="text-[#0369a1] font-semibold mb-2">📹 Join via Microsoft Teams</h3>
                <p className="text-gray-600 text-sm mb-4">Click the button below to join the meeting at your scheduled time:</p>
                <a 
                  href={settings.teams_meeting_link || '#'}
                  className="inline-block bg-[#0078d4] text-white px-6 py-3 rounded-md font-semibold hover:bg-[#106ebe] transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Join Teams Meeting
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminBookingSettingsPage;
